import { Link } from 'react-router-dom';
import Button from '../../../components/base/Button';

export default function HeroSection() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gray-950"
    >
      {/* Background image with dark overlay */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/27f09b638fd51744f9ad2e5d9ce34d25.jpg"
          alt="Las Vegas skyline"
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gray-950/80 via-gray-950/60 to-gray-950/90"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-gray-950/70 via-transparent to-gray-950/70"></div>
      </div>

      {/* Gold particle accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-24 left-16 w-64 h-64 bg-amber-500/8 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 right-12 w-80 h-80 bg-yellow-400/6 rounded-full blur-3xl"></div>
        <div className="absolute bottom-32 left-1/3 w-48 h-48 bg-amber-600/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-16 right-1/4 w-56 h-56 bg-amber-400/8 rounded-full blur-3xl"></div>
      </div>

      {/* Decorative gold lines */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/30 to-transparent"></div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-5xl mx-auto">

          {/* Badge */}
          <div className="inline-flex items-center px-5 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 mb-8 backdrop-blur-sm">
            <i className="ri-shield-star-line text-amber-400 mr-2 text-sm"></i>
            <span className="text-sm font-medium text-amber-300 tracking-wide">Trusted by 10,000+ Nevada Residents</span>
          </div>

          {/* Logo */}
          <div className="mb-8">
            <img
              src="https://static.readdy.ai/image/1aff587802347e8a3b9191c90eecded3/c67bf49259dce1e180324b942fef6f74.png"
              alt="Villarreal Enterprise Group Logo"
              className="h-20 w-auto mx-auto drop-shadow-2xl"
            />
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-white">
            Welcome to{' '}
            <span className="bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500 bg-clip-text text-transparent">
              Villarreal
            </span>
            <br />
            <span className="text-4xl md:text-5xl font-semibold text-gray-200">
              Enterprise Group
            </span>
          </h1>

          {/* Gold divider */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500"></div>
            <i className="ri-star-fill text-amber-400 text-xs"></i>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500"></div>
          </div>

          <p className="text-xl md:text-2xl mb-4 text-amber-300/90 font-medium tracking-wide">
            Parent company of Speedy DMV Services™ &amp; DMV-Xpress Services™
          </p>

          <p className="text-lg md:text-xl mb-12 text-gray-400 max-w-4xl mx-auto leading-relaxed">
            Nevada-based business group focused on simplifying essential services for the public —
            one vehicle, one document, and one client at a time.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <Link to="/services">
              <Button variant="primary" size="lg">
                <i className="ri-car-line mr-2"></i>
                Explore Our Services
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
            <Link to="/brands">
              <button className="whitespace-nowrap cursor-pointer px-8 py-4 text-lg font-semibold rounded-lg border-2 border-amber-500/50 text-amber-300 hover:bg-amber-500/10 hover:border-amber-400 transition-all duration-300 flex items-center">
                <i className="ri-building-2-line mr-2"></i>
                Our Brands
              </button>
            </Link>
            <Link to="/contact">
              <button className="whitespace-nowrap cursor-pointer px-8 py-4 text-lg font-semibold rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:text-white transition-all duration-300 flex items-center backdrop-blur-sm">
                <i className="ri-phone-line mr-2"></i>
                Contact Us
              </button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {[
              { value: '10,000+', label: 'Happy Clients', icon: 'ri-user-heart-line' },
              { value: '15+', label: 'Years Experience', icon: 'ri-award-line' },
              { value: '2', label: 'Trusted Brands', icon: 'ri-building-2-line' },
            ].map((stat, i) => (
              <div
                key={i}
                className="text-center p-6 rounded-xl bg-white/3 backdrop-blur-sm border border-amber-500/15 hover:border-amber-500/40 transition-all duration-300 group"
              >
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                  <i className={`${stat.icon} text-2xl text-amber-400 group-hover:text-amber-300 transition-colors`}></i>
                </div>
                <div className="text-3xl font-bold text-amber-400 mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-amber-500/40 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-amber-400/60 rounded-full mt-2"></div>
        </div>
      </div>
    </section>
  );
}
